// src/utils/pdfUtils.js
// Shared helpers and PDF generation for the Rental Listings report.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── shared formatters (used by both the page table and the PDF) ─────────────

// Decimal places per currency (ISO 4217)
const CURRENCY_DECIMALS = {
  JPY: 0, KRW: 0,
  BHD: 3, KWD: 3,
  // everything else defaults to 2
};

const getDecimalPlaces = (currency) =>
  CURRENCY_DECIMALS[currency] !== undefined ? CURRENCY_DECIMALS[currency] : 2;

/**
 * Format a Money object for **HTML display** (browser can render Unicode symbols).
 * Uses Intl.NumberFormat so the ₱, ¥, ₩ etc. symbols render correctly in the DOM.
 */
export const formatMoney = (money) => {
  if (!money) return '—';
  const { amount, currency } = money;
  if (amount === undefined || amount === null) return '—';

  const dp = getDecimalPlaces(currency);
  const value = amount / Math.pow(10, dp);

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
  } catch {
    return `${value.toFixed(dp)} ${currency}`;
  }
};

// ASCII-safe currency symbols that jsPDF Helvetica can render.
// Currencies with non-Latin symbols (₱ ¥ ₩ etc.) fall back to the ISO code.
const PDF_CURRENCY_SYMBOLS = {
  USD: '$',
  AUD: 'A$',
  EUR: 'EUR ',  // € is not in Helvetica
  GBP: 'GBP ',  // £ is actually in Helvetica, but we keep it safe
  PHP: 'PHP ',  // ₱ not in Helvetica — this was showing as ±
  JPY: 'JPY ',
  KRW: 'KRW ',
  BHD: 'BHD ',
  KWD: 'KWD ',
};

/**
 * Format a Money object for **PDF output** (jsPDF Helvetica — ASCII only).
 * Avoids Unicode symbols the font can't render.
 */
export const formatMoneyForPdf = (money) => {
  if (!money) return '—';
  const { amount, currency } = money;
  if (amount === undefined || amount === null) return '—';

  const dp = getDecimalPlaces(currency);
  const value = amount / Math.pow(10, dp);
  const prefix = PDF_CURRENCY_SYMBOLS[currency] || `${currency} `;
  const isNeg = value < 0;

  // Format absolute value with thousand separators
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });

  return `${isNeg ? '-' : ''}${prefix}${formatted}`;
};

/**
 * Build a display-friendly tenant name string from an array of renter summaries.
 */
export const formatTenants = (renters) => {
  if (!renters || renters.length === 0) return '—';
  return renters
    .map((r) => `${r.person?.given || r.given || ''} ${r.person?.surname || r.surname || ''}`.trim())
    .filter(Boolean)
    .join(', ');
};

// ─── PDF generation ──────────────────────────────────────────────────────────

// Brand colours (RGB)
const COLOURS = {
  emerald:    [16,  185, 129],   // #10B981
  red:        [220, 38,  38],    // #DC2626
  headerBg:   [31,  41,  55],    // #1F2937  (gray-800)
  headerText: [255, 255, 255],
  rowAlt:     [249, 250, 251],   // #F9FAFB  (gray-50)
  border:     [229, 231, 235],   // #E5E7EB  (gray-200)
  subText:    [107, 114, 128],   // #6B7280  (gray-500)
  negative:   [220, 38,  38],    // red-600
};

/**
 * Generate and download a PDF of the Rental Listings report.
 *
 * @param {Array}   tenancies   - Full array of tenancy objects from the API.
 * @param {Object}  opts
 * @param {boolean} opts.overdueOnly   - Whether the overdue filter was active.
 * @param {string}  [opts.agencyName] - Optional agency name for the header.
 */
export const generateRentalListingsPdf = (tenancies, { overdueOnly = false, agencyName = '' } = {}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const now = new Date();

  // ── header block ────────────────────────────────────────────────────────────
  // Dark background bar
  doc.setFillColor(...COLOURS.headerBg);
  doc.rect(0, 0, pageW, 28, 'F');

  // App / agency name (top-left)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLOURS.emerald);
  doc.text(agencyName || 'Rental Management System', 10, 9);

  // Report title
  doc.setFontSize(16);
  doc.setTextColor(...COLOURS.headerText);
  doc.text('Rental Listings Report', 10, 20);

  // Filter badge (top-right area of header)
  if (overdueOnly) {
    doc.setFillColor(...COLOURS.red);
    doc.roundedRect(pageW - 52, 8, 42, 8, 2, 2, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOURS.headerText);
    doc.text('OVERDUE ONLY', pageW - 31, 13.5, { align: 'center' });
  }

  // Generated date (top-right)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 180);
  const dateStr = now.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  doc.text(`Generated: ${dateStr} at ${timeStr}`, pageW - 10, 24, { align: 'right' });

  // ── summary line ─────────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setTextColor(...COLOURS.subText);
  doc.setFont('helvetica', 'normal');
  const filterLabel = overdueOnly ? 'overdue active listings' : 'active listings';
  doc.text(`${tenancies.length} ${filterLabel}`, 10, 36);

  // ── build table rows ─────────────────────────────────────────────────────────
  const rows = tenancies.map((t) => {
    const listing = t.rental_listing;
    const renters = t.rental_agreement?.renters || [];
    const propertyName = listing?.property_name || '—';
    const propertyAddress = listing?.property_address || '';
    const property = propertyAddress ? `${propertyName}\n${propertyAddress}` : propertyName;
    const room = listing?.room_number != null ? `Room ${listing.room_number}` : 'Whole property';
    const tenantNames = formatTenants(renters);
    const balance = formatMoneyForPdf(t.tenancy_ledger?.balance);
    const isNegative = (t.tenancy_ledger?.balance?.amount ?? 0) < 0;
    const status = (t.status || '').charAt(0).toUpperCase() + (t.status || '').slice(1);

    // Agreement type: combine payment_type + rent_frequency (e.g. "Prepay / Monthly")
    const paymentType = t.rental_agreement?.payment_type || '';
    const rentFreq = t.rental_agreement?.rent_frequency || '';
    const agreementType = [
      paymentType ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1) : '',
      rentFreq ? rentFreq.charAt(0).toUpperCase() + rentFreq.slice(1) : '',
    ].filter(Boolean).join(' / ') || '—';

    // Next rent due date
    const nextRentDue = t.next_rent_due_date || '—';

    return { cells: [property, room, tenantNames, agreementType, nextRentDue, balance, status], isNegative };
  });

  // ── autoTable ────────────────────────────────────────────────────────────────
  autoTable(doc, {
    startY: 40,
    head: [['Property', 'Room', 'Tenant(s)', 'Agreement Type', 'Next Rent Due', 'Balance', 'Status']],
    body: rows.map((r) => r.cells),
    margin: { left: 10, right: 10 },

    // Column widths (landscape A4 = 297 mm, minus 20 mm margins = 277 mm)
    columnStyles: {
      0: { cellWidth: 58 },  // Property
      1: { cellWidth: 22 },  // Room
      2: { cellWidth: 52 },  // Tenant(s)
      3: { cellWidth: 34 },  // Agreement Type
      4: { cellWidth: 26 },  // Next Rent Due
      5: { cellWidth: 45, halign: 'right' }, // Balance
      6: { cellWidth: 22 },  // Status
    },

    headStyles: {
      fillColor: COLOURS.headerBg,
      textColor: COLOURS.headerText,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
    },

    bodyStyles: {
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      textColor: [31, 41, 55],
      lineColor: COLOURS.border,
      lineWidth: 0.2,
    },

    alternateRowStyles: {
      fillColor: COLOURS.rowAlt,
    },

    // Colour negative balance cells red
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 5) {
        const rowMeta = rows[data.row.index];
        if (rowMeta?.isNegative) {
          data.cell.styles.textColor = COLOURS.negative;
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },

    // Page footer with page numbers
    didDrawPage: (_data) => {
      doc.setFontSize(7.5);
      doc.setTextColor(...COLOURS.subText);
      doc.setFont('helvetica', 'normal');
      const pageCount = doc.internal.getNumberOfPages();
      const pageNum  = doc.internal.getCurrentPageInfo().pageNumber;
      doc.text(`Page ${pageNum} of ${pageCount}`, pageW / 2, pageH - 6, { align: 'center' });
      doc.text('Rental Management System — Confidential', 10, pageH - 6);
    },
  });

  // ── filename and save ────────────────────────────────────────────────────────
  const datePart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const filterPart = overdueOnly ? 'overdue' : 'all';
  doc.save(`rental-listings-${filterPart}-${datePart}.pdf`);
};

